import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@ftedn/ui';
import { useApi } from '../../hooks/useApi';

interface Product {
  id: string;
  name: string;
  slug: string;
  tagline: string;
  description: string;
  status: string;
  category: string;
  imageUrl?: string;
  ingredientList?: string[];
  stats: { voteCount: number; ideaCount: number; feedbackCount: number; testerCount: number; reviewCount: number };
  publicTimeline: Array<{ id: string; title: string; description: string; date: string; status: string }>;
  gateArtifacts: { hasMinIdeas: boolean; hasIngredientList: boolean; hasFormulationApproval: boolean; hasMinBetaTesters: boolean };
  createdAt: string;
  updatedAt: string;
}

const STATUSES = ['concept', 'voting', 'beta', 'shipped', 'archived'] as const;
const STATUS_COLORS: Record<string, string> = {
  concept: '#6366f1', voting: '#f59e0b', beta: '#3b82f6', shipped: '#10b981', archived: '#6b7280',
};

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, post, patch } = useApi();
  const [product, setProduct] = useState<Product | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [timelineTitle, setTimelineTitle] = useState('');
  const [timelineDesc, setTimelineDesc] = useState('');

  const loadProduct = useCallback(async () => {
    try {
      const data = await get(`/products/${id}`);
      setProduct(data.product);
    } catch (e) { setError(String(e)); }
  }, [id]);

  useEffect(() => { loadProduct(); }, [loadProduct]);

  const doTransition = async (targetStatus: string) => {
    setActionMsg(null);
    try {
      const result = await post(`/products/${id}/transition`, { targetStatus });
      if (result.ok) {
        setActionMsg(`✅ Transitioned to ${targetStatus}`);
        loadProduct();
      } else {
        setActionMsg(`❌ ${result.reason}`);
      }
    } catch (e) { setActionMsg(`Error: ${String(e)}`); }
  };

  const toggleGate = async (gate: string, value: boolean) => {
    try {
      await patch(`/products/${id}/gates`, { gate, value });
      loadProduct();
    } catch (e) { setActionMsg(`Gate update failed: ${String(e)}`); }
  };

  const addTimeline = async () => {
    if (!timelineTitle || !timelineDesc || !product) return;
    try {
      await post(`/products/${id}/timeline`, {
        title: timelineTitle,
        description: timelineDesc,
        date: new Date().toISOString().slice(0, 10),
        status: product.status,
      });
      setTimelineTitle('');
      setTimelineDesc('');
      setActionMsg('✅ Timeline entry added');
      loadProduct();
    } catch (e) { setActionMsg(`Timeline failed: ${String(e)}`); }
  };

  if (error) return <Card><p style={{ color: 'var(--color-error)' }}>Error: {error}</p></Card>;
  if (!product) return <Card><p>Loading product…</p></Card>;

  return (
    <div>
      <button onClick={() => navigate('/products')} style={{
        background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)',
        marginBottom: '1rem', fontSize: '0.9rem',
      }}>← Back to Products</button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0 }}>{product.name}</h1>
        <span style={{
          padding: '4px 14px', borderRadius: 14, fontSize: '0.85rem', fontWeight: 700,
          background: `${STATUS_COLORS[product.status]}20`, color: STATUS_COLORS[product.status],
        }}>
          {product.status.toUpperCase()}
        </span>
      </div>

      {actionMsg && <Card><p>{actionMsg}</p></Card>}

      {/* Info */}
      <Card>
        <h2>Details</h2>
        <p><strong>Slug:</strong> {product.slug}</p>
        <p><strong>Tagline:</strong> {product.tagline}</p>
        <p><strong>Category:</strong> {product.category}</p>
        <p><strong>Description:</strong> {product.description}</p>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
          Created: {new Date(product.createdAt).toLocaleDateString()} • 
          Updated: {new Date(product.updatedAt).toLocaleDateString()}
        </p>
      </Card>

      {/* Stats */}
      <Card>
        <h2>Stats</h2>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
          {Object.entries(product.stats).map(([key, val]) => (
            <div key={key} style={{ textAlign: 'center' }}>
              <p style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>{val}</p>
              <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem', margin: 0 }}>
                {key.replace('Count', '')}
              </p>
            </div>
          ))}
        </div>
      </Card>

      {/* Gate Artifacts */}
      <Card>
        <h2>Gate Artifacts</h2>
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          {Object.entries(product.gateArtifacts).map(([gate, met]) => (
            <label key={gate} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={met as boolean}
                onChange={() => toggleGate(gate, !(met as boolean))}
              />
              <span style={{ fontWeight: met ? 600 : 400, color: met ? 'var(--color-success, #10b981)' : undefined }}>
                {gate.replace(/([A-Z])/g, ' $1').replace('has ', '').trim()}
              </span>
            </label>
          ))}
        </div>
      </Card>

      {/* Transitions */}
      <Card>
        <h2>Lifecycle Transitions</h2>
        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
          Force-transition this product (admin override). Gating predicates are still checked.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {STATUSES.filter(s => s !== product.status).map((s) => (
            <button
              key={s}
              onClick={() => doTransition(s)}
              style={{
                padding: '8px 16px', borderRadius: 8, border: '2px solid',
                borderColor: STATUS_COLORS[s], background: 'transparent',
                color: STATUS_COLORS[s], fontWeight: 600, cursor: 'pointer',
                fontSize: '0.85rem', transition: 'all 0.2s',
              }}
            >
              → {s.toUpperCase()}
            </button>
          ))}
        </div>
      </Card>

      {/* Timeline */}
      <Card>
        <h2>Public Timeline ({product.publicTimeline.length} entries)</h2>
        {product.publicTimeline.length > 0 && (
          <ul style={{ marginBottom: '1rem' }}>
            {product.publicTimeline.map((e) => (
              <li key={e.id} style={{ marginBottom: '0.5rem' }}>
                <strong>{e.title}</strong> — {e.description}
                <span style={{ color: 'var(--color-text-secondary)', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
                  ({e.date})
                </span>
              </li>
            ))}
          </ul>
        )}
        <h3>Add Entry</h3>
        <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
          <input
            placeholder="Title"
            value={timelineTitle}
            onChange={(e) => setTimelineTitle(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: 6,
              border: '1px solid var(--color-border, #d1d5db)',
            }}
          />
          <input
            placeholder="Description"
            value={timelineDesc}
            onChange={(e) => setTimelineDesc(e.target.value)}
            style={{
              padding: '8px 12px', borderRadius: 6,
              border: '1px solid var(--color-border, #d1d5db)',
            }}
          />
          <button
            onClick={addTimeline}
            disabled={!timelineTitle || !timelineDesc}
            style={{
              padding: '8px 16px', borderRadius: 6, border: 'none',
              background: 'var(--color-primary, #18181b)', color: '#fff',
              cursor: 'pointer', fontWeight: 600, alignSelf: 'flex-start',
            }}
          >
            Add Timeline Entry
          </button>
        </div>
      </Card>
    </div>
  );
}
